import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { AccordionDetails } from '@mui/material';
import MuiAccordion from '@mui/material/Accordion';
import IconButton from '@mui/material/IconButton';
import { withStyles } from '@mui/styles';
import Dropdown from '@/components/UI/Dropdown/Dropdown';
import InputText from '@/components/UI/InputText/InputText';
import SimpleButton from '@/components/UI/SimpleButton/SimpleButton';
import { CR_PERMISSION_ACTIONS } from '@/helpers/crPermissions';
import useCRPermissionMatrix from '@/hooks/useCRPermissionMatrix';
import useDetectLanguage from '@/hooks/useDetectLanguage';
import { getCampaignInfo, getCampaignValidations } from '@/redux/Campaign/campaign.reducer';
import { getHeaderLanguage } from '@/redux/Header/header.reducer';
import { OPTIONS } from '../../volumeDetails-constants';
import styles from '../../VolumeDetails.module.scss';

const Accordion = withStyles({
  root: {
    width: '100%',
    border: '1px solid rgba(0, 0, 0, .125)',
    boxShadow: 'none',
    '&:not(:last-child)': {
      borderBottom: 0,
    },
    '&:before': {
      display: 'none',
    },
    '&$expanded': {
      margin: 'auto',
    },
  },
  expanded: {},
})(MuiAccordion);

const RuleAccordion = (props) => {
  const { plantSelected, id, rule, onChange, headerLanguage, campaignInfo, defaultLanguage, validations,
    validationActive, onDelete } = props;
  const [expanded, setExpanded] = useState(true);
  const { t } = useTranslation();
  const { isButtonDisabled, isReadOnly } = useCRPermissionMatrix(campaignInfo.status, campaignInfo.authorization);
  const checkDisabled = isReadOnly() || isButtonDisabled(CR_PERMISSION_ACTIONS.SAVE) || (headerLanguage !== defaultLanguage);
  const { detectLanguage } = useDetectLanguage();
  const ruleValidation = validations?.affectedVehicles?.[headerLanguage]?.volumes?.[plantSelected?.wkzNumber]?.codeRules[id];
  const errorClass = validationActive && ruleValidation?.invalid ? 'error' : '';

  const onBlur = (name, value) => {
    detectLanguage(value);
  };

  return (
    <Accordion expanded={expanded}>
      <div className={styles.AccordionHeader}>
        <div className={styles.TitleContainer}>
          <IconButton id={`open-rule-${id + 1}`} size="small" onClick={() => { setExpanded(!expanded); }}>
            {expanded && <i className={`di icon-pfeil-chevron-oben ${styles.Icon} `} />}
            {!expanded && <i className={`di icon-pfeil-chevron-unten ${styles.Icon} `} />}
          </IconButton>
          <div className={`${styles.Title} ${errorClass}`}>{`${t('codeRule.rule')} ${id + 1}`}</div>
        </div>
        <SimpleButton
          disabled={checkDisabled}
          size="small"
          color="primary"
          buttonText={t('codeRule.deleteRule')}
          handleClick={() => onDelete(id)}
        />
      </div>
      <AccordionDetails>
        <div className={styles.TabContainer}>
          <div className={styles.InputsContainer}>
            <div className={styles.OneColumnsWithMarginRight}>
              <Dropdown
                disabled={checkDisabled}
                id={`rule-selection-${id}`}
                label={t('codeRule.selection')}
                options={OPTIONS}
                language={headerLanguage}
                value={rule.codeRulesSelectionAccording}
                handleChange={(event) => onChange('update', id, 'codeRulesSelectionAccording', event.target.value)}
              />
              <InputText
                isMandatory
                invalid={validationActive && ruleValidation?.codeRulesDescription?.invalid}
                disabled={checkDisabled}
                id={`rule-code-${id}`}
                label={t('codeRule.code')}
                value={rule.codeRulesDescription}
                handleChange={(event) => onChange('update', id, 'codeRulesDescription', event.target.value)}
                fieldPath={['affectedVehicles', 'limitationVolumeManualEntry', `codeRules[${id}]`, 'description']}
                noTranslationField
                maxLength={1024}
              />
            </div>
            <div className={styles.OneColumns}>
              <InputText
                disabled={checkDisabled}
                id={`rule-comment-${id}`}
                multiline
                maxRows={5}
                invalid={validationActive && ruleValidation?.codeRuleComment?.invalid}
                rows={5}
                label={t('codeRule.comment')}
                value={rule.codeRuleComment.translations[headerLanguage]}
                handleChange={(event) => onChange('update', id, 'codeRuleComment', event.target.value)}
                onBlur={(ev) => onBlur('comment', ev.target.value)}
                fieldPath={['affectedVehicles', 'limitationVolumeManualEntry', `codeRules[${id}]`, 'comment']}
                maxLength={256}
                helperText={(!checkDisabled && rule.codeRuleComment.translations[headerLanguage]?.length >= 256)
                  ? [t('validations.maxLength', { maxLength: 256 })]
                  : []}
              />
            </div>
          </div>
        </div>
      </AccordionDetails>
    </Accordion>
  );
};

const mapStateToProps = (state) => ({
  headerLanguage: getHeaderLanguage(state) || getCampaignInfo(state)?.defaultLanguage,
  campaignInfo: getCampaignInfo(state),
  defaultLanguage: getCampaignInfo(state)?.defaultLanguage,
  validations: getCampaignValidations(state),
  validationActive: getCampaignValidations(state)?.active?.all,
});

export default connect(mapStateToProps)(RuleAccordion);
